import type {
  TaskInstanceStatus,
  TaskResult,
  WorkflowPriority,
} from '../../../types/workflow-v9';
import type {
  BlueprintChecklistItem,
  BlueprintFormField,
  BlueprintStageRules,
  BlueprintExitOptions,
} from '../../../types/processBlueprint';

// ============================================================================
// TASK LIST ITEM — usado em Gestao e Minha Mesa
// ============================================================================

export interface TaskListItem {
  id: string;
  instanceId: string;
  instanceTitle?: string;
  referenceCode?: string;
  stageId: string;
  stageName: string;
  status: TaskInstanceStatus;
  assignedRoleId?: string | null;
  assignedRoleKey?: string | null;
  assignedRoleName?: string | null;
  assignedUserId?: string | null;
  assignedUserName?: string | null;
  assignedTo?: string | null;
  assignedToName?: string | null;
  claimedAt?: string | null;
  isRoleQueue?: boolean;
  priority?: WorkflowPriority;
  startedAt?: string | null;
  completedAt?: string | null;
  availableAt?: string | null;
  slaDeadline?: string | null;
  slaWarningAt?: string | null;
  isOverdue: boolean;
  createdAt: string;
  updatedAt: string;
  organizationId: string;
  result?: TaskResult | null;
  notes?: string | null;
  metadata?: TaskMetadata | null;
  // Yamazumi ↔ Execução
  plannedDuration?: number | null;     // minutos planejados (do mapa/yamazumi)
  plannedRoleId?: string | null;       // cargo planejado (do yamazumi)
  actualDuration?: number | null;      // minutos reais medidos
  queueDuration?: number | null;       // minutos em fila antes de iniciar
}

// ============================================================================
// TASK METADATA — snapshot do blueprint dentro de cada task
// ============================================================================

export interface TaskMetadata {
  blueprint_stage_order?: number;
  checklist?: BlueprintChecklistItem[];
  formFields?: BlueprintFormField[];
  instruction?: string | null;
  completionType?: 'simple_complete' | 'approve_reject';
  exitOptions?: BlueprintExitOptions;
  rules?: BlueprintStageRules;
  stage_name?: string;
  role?: string;
  roleId?: string;
  roleName?: string;
  version?: string;
  formData?: Record<string, unknown>;
  checklistData?: Record<string, boolean>;
  operationalMetrics?: TaskOperationalMetrics;
  operationalHistory?: TaskOperationalHistoryEntry[];
  [key: string]: unknown;
}

export interface TaskOperationalMetrics {
  enteredAt?: string | null;
  claimedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  tpRealMinutes?: number | null;
  traRealMinutes?: number | null;
  queueMinutes?: number | null;
  waitingBeforeClaimMinutes?: number | null;
  updatedAt?: string | null;
}

export interface TaskOperationalHistoryEntry extends TaskOperationalMetrics {
  event: 'activated' | 'claimed' | 'started' | 'paused' | 'resumed' | 'completed' | 'failed' | 'skipped';
  capturedAt: string;
  result?: TaskResult | null;
}

// ============================================================================
// TASK DETAIL — item completo com metadata parsed
// ============================================================================

export interface TaskDetailItem extends TaskListItem {
  checklist: BlueprintChecklistItem[];
  formFields: BlueprintFormField[];
  instruction: string | null;
  completionType: 'simple_complete' | 'approve_reject';
  exitOptions: BlueprintExitOptions;
  rules: BlueprintStageRules;
  formData: Record<string, unknown>;
  checklistData: Record<string, boolean>;
}

// ============================================================================
// PAYLOADS DE MUTAÇÃO
// ============================================================================

export interface TaskCompletionPayload {
  taskId: string;
  result: TaskResult;
  notes?: string;
  formData?: Record<string, unknown>;
  checklistData?: Record<string, boolean>;
  completedBy: string;
  previousUpdatedAt: string; // For optimistic locking
  /** ID da versão do blueprint ativa — armazenado em todas as respostas para auditoria */
  blueprintVersionId?: string;
}

export interface TaskReassignPayload {
  taskId: string;
  newAssignee: string;
  justification: string;
  performedBy: string;
}

export interface TaskClaimPayload {
  taskId: string;
  userId: string;
  userName?: string;
}

export interface TaskSkipPayload {
  taskId: string;
  justification: string;
  performedBy: string;
}

export interface TaskSLAAdjustPayload {
  taskId: string;
  newDeadline: string;
  justification: string;
  performedBy: string;
}

// ============================================================================
// FILTROS
// ============================================================================

export interface TaskFilters {
  status: TaskInstanceStatus[];
  priority: WorkflowPriority[];
  search: string;
  overdue: boolean;
  assignee?: string;
  stageName?: string;
  instanceId?: string;
}

export type TaskSortField = 'sla_deadline' | 'created_at' | 'updated_at' | 'stage_name' | 'status' | 'priority';
export type SortDirection = 'asc' | 'desc';

// ============================================================================
// RESULTADOS DE LISTA
// ============================================================================

export interface TaskListResult {
  items: TaskListItem[];
  total: number;
  page: number;
  pageSize: number;
}

// ============================================================================
// HELPER — parseia metadata raw em TaskDetailItem
// ============================================================================

export function parseTaskDetail(task: TaskListItem): TaskDetailItem {
  const meta = task.metadata ?? {};
  return {
    ...task,
    checklist: meta.checklist ?? [],
    formFields: meta.formFields ?? [],
    instruction: meta.instruction ?? null,
    completionType: meta.completionType ?? 'simple_complete',
    exitOptions: meta.exitOptions ?? {},
    rules: meta.rules ?? {},
    formData: (meta.formData as Record<string, unknown>) ?? {},
    checklistData: (meta.checklistData as Record<string, boolean>) ?? {},
  };
}
